// RFC 4180 CSV encoder. Every field is double-quote-wrapped and
// internal quotes are escaped as "". Lines terminate with CRLF.
// Delimiter "," by default (the Observaciones export); the «Comparar
// tiendas» export passes ";" + decimal comma, which is what a
// spreadsheet set to es-CL reads as columns and numbers (ADR-0167).
// The downloader uses a Blob + ObjectURL anchor click and revokes
// the URL afterwards.

// RFC-4180 quoting alone doesn't stop Excel/Sheets from evaluating a
// field starting with one of these as a formula on open — a provider's
// free-text observation (humanDescription) is untrusted input that
// reaches this export unmodified. Prefixing with a single quote is the
// standard mitigation: Excel/Sheets render the cell as text without
// the leading quote. Only applied to genuine string fields — a numeric
// metric legitimately starting with "-" must stay a real number, not
// get coerced into text.
const FORMULA_TRIGGER_CHARS = new Set(["=", "+", "-", "@", "\t", "\r"]);

export type CsvOptions = {
  /** Field separator. Default ",". */
  delimiter?: string;
  /** Write numbers with a decimal comma (4,2) instead of a point. */
  decimalComma?: boolean;
};

function csvField(value: string | number | null | undefined, decimalComma: boolean): string {
  if (value === null || value === undefined) return '""';
  let str =
    typeof value === "number" && decimalComma ? String(value).replace(".", ",") : String(value);
  if (typeof value === "string" && str.length > 0 && FORMULA_TRIGGER_CHARS.has(str[0]!)) {
    str = `'${str}`;
  }
  return `"${str.replace(/"/g, '""')}"`;
}

export function toRfc4180(
  rows: ReadonlyArray<ReadonlyArray<string | number | null | undefined>>,
  options: CsvOptions = {}
): string {
  const delimiter = options.delimiter ?? ",";
  const decimalComma = options.decimalComma ?? false;
  return rows.map((row) => row.map((v) => csvField(v, decimalComma)).join(delimiter)).join("\r\n");
}

export function downloadCsv(content: string, filename: string): void {
  // BOM-prefixed so Excel opens the file with the correct encoding
  // when the user double-clicks the download.
  downloadBlob(new Blob(["﻿" + content], { type: "text/csv;charset=utf-8;" }), filename);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Civil date in Chile, not UTC: after ~20:00 CLT the UTC date is already
// tomorrow, and a file downloaded tonight must not be named after it.
const SANTIAGO_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Santiago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** YYYY-MM-DD in America/Santiago for `at` (default: now). */
export function todayIsoDate(at: Date = new Date()): string {
  return SANTIAGO_DAY.format(at);
}
